import React from "react";

/**
 * Componente ExpensesListCard para mostrar lista de gastos con acciones
 * 
 * @param {Object} props
 * @param {string} props.title - Título de la tarjeta
 * @param {Array} props.expenses - Array de gastos a mostrar
 * @param {Function} props.formatAmount - Función para formatear montos
 * @param {string} props.color - Clase de gradiente de color (ej: "from-orange-500 to-red-500")
 * @param {React.ReactElement} props.icon - Icono a mostrar
 * @param {Function} props.onEditExpense - Función para editar un gasto
 * @param {Function} props.onDeleteExpense - Función para eliminar un gasto
 * @param {Function} props.onToggleStatus - Función para cambiar estado pagado/pendiente
 * @param {string} props.emptyMessage - Mensaje cuando no hay gastos
 */
const ExpensesListCard = ({ 
  title, 
  expenses, 
  formatAmount, 
  color, 
  icon, 
  onEditExpense, 
  onDeleteExpense, 
  onToggleStatus, 
  emptyMessage 
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  // Agrupar totales por moneda
  const totalsByCurrency = expenses.reduce((acc, exp) => {
    const currency = exp.currency || "PEN";
    if (!acc[currency]) acc[currency] = 0;
    acc[currency] += exp.amount || 0;
    return acc;
  }, {});

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
      <div 
        className={`bg-gradient-to-r ${color} p-4 cursor-pointer hover:opacity-95 transition-opacity`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/30 flex items-center justify-center">
              {React.cloneElement(icon, { className: "w-5 h-5 text-white" })}
            </div>
            <div>
              <h3 className="text-base font-bold text-white">{title}</h3>
              <p className="text-xs text-white/90">{expenses.length} {expenses.length === 1 ? 'gasto' : 'gastos'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              {Object.keys(totalsByCurrency).length > 0 ? (
                <div className="space-y-1">
                  {Object.entries(totalsByCurrency).map(([currency, total]) => (
                    <p key={currency} className="text-lg font-bold text-white">
                      {formatAmount ? formatAmount(total, false, currency) : `${currency} ${total}`}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-lg font-bold text-white">
                  {formatAmount ? formatAmount(0) : "0"}
                </p>
              )}
            </div>
            <svg 
              className={`w-5 h-5 text-white transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {expenses.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium">{emptyMessage}</p>
            </div>
          ) : (
            expenses.map((expense) => (
              <div
                key={expense.id}
                className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm text-gray-800 truncate">
                        {expense.name}
                      </h4>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="font-bold text-gray-900">
                        {formatAmount ? formatAmount(expense.amount, false, expense.currency || "PEN") : expense.amount}
                      </span>
                      {expense.currency && expense.currency !== "PEN" && (
                        <span className="text-gray-500 text-xs">({expense.currency})</span>
                      )}
                      {expense.dueDay && (
                        <span className="text-orange-600">
                          Día {expense.dueDay}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    {/* Botón marcar como pagado/pendiente */}
                    {onToggleStatus && (
                      <button
                        onClick={() => onToggleStatus(expense.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 ${
                          expense.status === "PENDING"
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-orange-500 hover:bg-orange-600 text-white"
                        }`}
                        aria-label={expense.status === "PENDING" ? "Marcar como pagado" : "Deshacer pago"}
                      >
                        {expense.status === "PENDING" ? (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Pagar
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                            </svg>
                            Revertir
                          </>
                        )}
                      </button>
                    )}

                    {/* Botones de acción */}
                    <div className="flex items-center gap-0.5">
                      {/* Botón editar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditExpense && onEditExpense(expense.id);
                        }}
                        className="p-1.5 hover:bg-blue-100 rounded transition-colors text-blue-600"
                        aria-label="Editar gasto"
                        title="Editar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      {/* Botón eliminar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteExpense && onDeleteExpense(expense.id, expense.name);
                        }}
                        className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600"
                        aria-label="Eliminar gasto"
                        title="Eliminar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ExpensesListCard;
